/**
 * useLoungeMeta - Time & Streak Awareness Hook (OPTIMIZED)
 * 
 * Purpose: Provide clean source of truth for lounge time limits and streak progress
 * Displays badges showing minutes remaining and streak status
 * 
 * Phase 3 - Transparency layer for Lounge UX V2
 * 
 * OPTIMIZATION:
 * - Time status: polled every 30s (changes frequently)
 * - Streak status: fetched once on mount (changes max once per day)
 * - Decoupled polling: streak doesn't hammer the backend
 */

import { useEffect, useState, useCallback } from "react";
import apiClient from "../api/client";


/**
 * Hook for lounge time and streak metadata
 * 
 * Features:
 * - Fetches remaining time (polled every 30s)
 * - Fetches streak data (once on mount, then every 10 min)
 * - Calculates derived values (minutes, progress)
 * - Provides UI-ready states
 */
export function useLoungeMeta({ 
  token,
  enabled = true, 
  timePollMs = 30000,    // Poll time every 30s
  streakRefreshMs = 600000  // Refresh streak every 10 min
} = {}) {
  const [timeMeta, setTimeMeta] = useState({
    remainingSecondsToday: null,
    maxDailySeconds: null,
  });
  
  const [streakMeta, setStreakMeta] = useState({
    current: null,
    best: null,
    lastDate: null,
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch time status ONLY (lightweight, can poll frequently)
   */
  const fetchTimeStatus = useCallback(async () => {
    if (!token || !enabled) return;
    
    try {
      const loungeRes = await apiClient.get(`/lounge/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const loungeData = loungeRes.data || {};
      
      setTimeMeta({
        remainingSecondsToday: loungeData.remaining_seconds ?? null,
        maxDailySeconds: loungeData.max_daily_seconds ?? null,
      });

    } catch (err) {
      console.error('[useLoungeMeta] Time fetch error:', err);
    }
  }, [token, enabled]);

  /**
   * Fetch streak status (optimized endpoint, cheap read)
   */
  const fetchStreakStatus = useCallback(async () => {
    if (!token || !enabled) return;
    
    try {
      const streakRes = await apiClient.get(`/streak/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const streakData = streakRes.data || {};
      
      setStreakMeta({
        current: streakData.streak_current ?? 0,
        best: streakData.streak_best ?? 0,
        lastDate: streakData.streak_last_date ?? null,
      });
      
      console.log('[useLoungeMeta] Streak fetched:', streakData);

    } catch (err) {
      console.warn('[useLoungeMeta] Streak fetch error (non-critical):', err);
      // Set safe defaults on error
      setStreakMeta({
        current: 0,
        best: 0,
        lastDate: null,
      });
    }
  }, [token, enabled]);

  /**
   * Initial fetch on mount: get both time and streak
   */
  useEffect(() => {
    if (!token || !enabled) return;
    
    setLoading(true);
    
    Promise.all([
      fetchTimeStatus(),
      fetchStreakStatus(),
    ]).finally(() => {
      setLoading(false);
    });
  }, [token, enabled, fetchTimeStatus, fetchStreakStatus]);

  /**
   * Poll time status every 30s (time changes frequently)
   */
  useEffect(() => {
    if (!enabled || !token) return;
    
    const timeIntervalId = setInterval(() => {
      fetchTimeStatus();
    }, timePollMs);

    return () => clearInterval(timeIntervalId);
  }, [enabled, token, fetchTimeStatus, timePollMs]);
  
  /**
   * Refresh streak status every 10 minutes (changes max once per day)
   */
  useEffect(() => {
    if (!enabled || !token) return;
    
    const streakIntervalId = setInterval(() => {
      fetchStreakStatus();
    }, streakRefreshMs);

    return () => clearInterval(streakIntervalId);
  }, [enabled, token, fetchStreakStatus, streakRefreshMs]);

  // ==================== DERIVED VALUES ====================

  /**
   * Remaining minutes (rounded up)
   * null if unlimited (Elite users)
   */
  const remainingMinutes = timeMeta.remainingSecondsToday != null
    ? Math.max(0, Math.ceil(timeMeta.remainingSecondsToday / 60))
    : null;

  /**
   * Daily limit in minutes
   */
  const dailyLimitMinutes = timeMeta.maxDailySeconds != null
    ? Math.ceil(timeMeta.maxDailySeconds / 60)
    : null;

  /**
   * Out of time flag
   */
  const isOutOfTime =
    timeMeta.remainingSecondsToday != null && timeMeta.remainingSecondsToday <= 0;

  /**
   * Streak progress (0.0 to 1.0)
   */
  const streakGoal = 5; // 5-day streak for bonus
  const streakProgress =
    streakMeta.current != null
      ? Math.min(1, streakMeta.current / streakGoal)
      : 0;

  /**
   * Streak label for display
   */
  const streakLabel =
    streakMeta.current != null
      ? `${streakMeta.current}/${streakGoal}`
      : null;

  /**
   * Low time warning (< 3 minutes)
   */
  const isLowTime = remainingMinutes != null && remainingMinutes > 0 && remainingMinutes <= 3;

  return {
    // Raw data (separated for clarity)
    timeMeta,
    streakMeta,
    loading,
    error,
    
    // Actions
    refreshTime: fetchTimeStatus,
    refreshStreak: fetchStreakStatus,
    
    // Derived values (UI-ready)
    remainingMinutes,
    dailyLimitMinutes,
    isOutOfTime,
    isLowTime,
    streakProgress,
    streakLabel,
  };
}
